import { PublicHostedAuthSession } from '@unipile/provider-shared';
import type { ResendCheckpointBody } from '@unipile/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '../../../libs/axios';
import type { MutationConfig } from '../../../types/react-query-utility';

/**
 * This hook must live in UI package because it's used in provider packages.
 */

export const resendCheckpoint = async (
  { data }: { data: ResendCheckpointBody },
  { signal }: { signal?: AbortSignal } = {},
): Promise<void> => {
  await axiosInstance.post<void>(`/v2/auth/checkpoint/resend`, data, { signal });
};

type UseResendCheckpointOptions = {
  mutationConfig?: MutationConfig<typeof resendCheckpoint>;
  /**
   * Resolved when the mutation runs. Use when the abort controller is recreated (e.g. in an effect)
   * so Strict Mode’s effect re-run does not keep using an already-aborted signal.
   */
  getSignal?: () => AbortSignal | undefined;
};

export const useResendCheckpoint = ({ mutationConfig, getSignal }: UseResendCheckpointOptions = {}) => {
  const { onSuccess, ...restConfig } = mutationConfig || {};

  const q = useQueryClient();

  return useMutation({
    onSuccess: (...args) => {
      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: ({ data }: { data: Omit<ResendCheckpointBody, 'state'> }): Promise<void> => {
      // Result of useHostedAuthPayload should be in react-query's cache as it's the first query made when loading the page
      const session = q.getQueryData<PublicHostedAuthSession>(['session']);
      if (!session) throw new Error('Session not in cache');

      return resendCheckpoint({ data: { ...data, ...(session.state && { state: session.state }) } }, { signal: getSignal?.() });
    },
  });
};
